import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/api';

const API_URL = `${API_BASE_URL}/api`;

const CreateSchemeDialog = ({ open, onClose, customer, onSuccess }) => {
  const [formData, setFormData] = useState({
    monthlyInstallment: '',
    startDate: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.monthlyInstallment || parseFloat(formData.monthlyInstallment) <= 0) {
      setError('Please enter a valid monthly installment amount');
      return;
    }

    if (!formData.startDate) {
      setError('Please select a start date');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/schemes`,
        {
          customerTokenNo: customer.TokenNo,
          monthlyInstallment: parseFloat(formData.monthlyInstallment),
          startDate: formData.startDate
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Scheme created successfully!');

      // Reset form
      setFormData({
        monthlyInstallment: '',
        startDate: new Date().toISOString().split('T')[0]
      });

      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating scheme:', err);
      const errorMessage = err.response?.data?.error?.message || 'Failed to create scheme';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!customer) return null;

  // Calculate expiry date (24 months from start)
  const calculateExpiryDate = () => {
    if (!formData.startDate) return '';
    const start = new Date(formData.startDate);
    const expiry = new Date(start);
    expiry.setMonth(expiry.getMonth() + 24);
    return expiry.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Calculate total contribution (13 payments)
  const totalContribution = formData.monthlyInstallment 
    ? (parseFloat(formData.monthlyInstallment) * 13).toFixed(2)
    : '0.00';

  // Calculate total credit (contribution + bonus)
  const totalCredit = formData.monthlyInstallment 
    ? (parseFloat(formData.monthlyInstallment) * 14).toFixed(2)
    : '0.00';

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a1a",
          border: "1px solid #333",
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#2b2b2b',
        borderBottom: '1px solid #333'
      }}>
        <Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#C59A58' }}>
            Create New Scheme
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            {customer.CustomerName} • Token #{customer.TokenNo}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#C59A58' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, bgcolor: "#1a1a1a" }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              bgcolor: "#2a1a1a",
              color: "#fff",
              border: "1px solid #d32f2f",
              '& .MuiAlert-icon': { color: "#d32f2f" }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Scheme Info */}
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            bgcolor: "#1a2a2a",
            color: "#fff",
            border: "1px solid #2196f3",
            '& .MuiAlert-icon': { color: "#2196f3" }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#C59A58" }}>
            Scheme Details:
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: "#9CA3AF" }}>
            • Duration: 24 months
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: "#9CA3AF" }}>
            • Required Payments: 13 monthly installments
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: "#9CA3AF" }}>
            • Bonus: 1 month installment (after 13 payments)
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: "#9CA3AF" }}>
            • Usage Phase: 11 months after bonus
          </Typography>
        </Alert>

        {/* Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            required
            label="Monthly Installment"
            type="number"
            value={formData.monthlyInstallment}
            onChange={handleChange('monthlyInstallment')}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1, color: "#C59A58" }}>₹</Typography>
            }}
            helperText="Amount customer will pay each month"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                '& input': { color: '#fff' }
              },
              '& .MuiInputLabel-root': { 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': { color: '#C59A58' }
              },
              '& .MuiFormHelperText-root': { color: '#9CA3AF' }
            }}
          />

          <TextField
            fullWidth
            required
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={handleChange('startDate')}
            InputLabelProps={{
              shrink: true
            }}
            helperText="Scheme start date"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                '& input': { color: '#fff' }
              },
              '& .MuiInputLabel-root': { 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': { color: '#C59A58' }
              },
              '& .MuiFormHelperText-root': { color: '#9CA3AF' }
            }}
          />
        </Box>

        {/* Calculation Summary */}
        {formData.monthlyInstallment && parseFloat(formData.monthlyInstallment) > 0 && (
          <Paper sx={{ 
            p: 2, 
            mt: 3, 
            bgcolor: '#2a2a2a',
            border: '1px solid #333',
            borderRadius: 2
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#C59A58' }}>
              Scheme Summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>Monthly Installment:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                  ₹{parseFloat(formData.monthlyInstallment).toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>Total Contribution (13 months):</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                  ₹{totalContribution}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>Bonus (1 month):</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800' }}>
                  ₹{parseFloat(formData.monthlyInstallment).toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #333' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#9CA3AF' }}>Total Credit Available:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#C59A58', fontSize: '1.1rem' }}>
                  ₹{totalCredit}
                </Typography>
              </Box>
              {formData.startDate && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Expiry Date:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#fff' }}>
                    {calculateExpiryDate()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        bgcolor: '#2b2b2b', 
        borderTop: '1px solid #333' 
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            color: '#9CA3AF',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} sx={{ color: "#000" }} /> : <AddIcon />}
          onClick={handleSubmit}
          disabled={loading || !formData.monthlyInstallment || !formData.startDate}
          sx={{ bgcolor: '#C59A58', '&:hover': { bgcolor: '#b68649' }, color: '#000', fontWeight: 600 }}
        >
          {loading ? 'Creating...' : 'Create Scheme'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSchemeDialog;
